// This optional code is used to register a service worker.
// register() is not called by default.

import { Workbox, messageSW } from 'workbox-window';

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log(
            '🔧 This web app is being served cache-first by a service worker.'
          );
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  const wb = new Workbox(swUrl);

  wb.addEventListener('installed', (event) => {
    if (event.isUpdate) {
      console.log('🔄 New service worker installed, update available');

      if (config && config.onUpdate) {
        config.onUpdate(event.sw as any);
      }
    } else {
      console.log('✅ Service worker installed successfully');

      if (config && config.onSuccess) {
        config.onSuccess(event.sw as any);
      }
    }
  });

  wb.addEventListener('waiting', () => {
    console.log('⏳ New service worker waiting to activate');
  });

  wb.addEventListener('controlling', () => {
    console.log('✅ Service worker is now controlling the page');
    window.location.reload();
  });

  wb.addEventListener('activated', (event) => {
    if (!event.isUpdate) {
      console.log('✅ Service worker activated');
    }
  });

  wb.register().catch((error) => {
    console.error('❌ Error during service worker registration:', error);
  });

  // Make workbox available globally for manual updates
  (window as any).workbox = wb;
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('📴 No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

// Helper function to skip waiting and activate new service worker
export function skipWaiting() {
  const wb = (window as any).workbox as Workbox;
  if (wb) {
    wb.addEventListener('controlling', () => {
      window.location.reload();
    });

    wb.messageSkipWaiting();
  }
}

// Helper function to check for updates manually
export async function checkForUpdates(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    await registration.update();
    return !!registration.waiting;
  }
  return false;
}
